<template>
  <picture>
    <source
      v-if="fullWidth"
      :srcset="getSources[4]"
      media="(min-width: 1540px)"
      :width="desktopDimensions[0] || defaultDimensions[4]"
      :height="desktopDimensions[1] || defaultDimensions[4]"
    />
    <source
      :srcset="getSources[3]"
      media="(min-width: 1024px)"
      :width="tabletDimensions[0] || desktopDimensions[0] || defaultDimensions[3]"
      :height="tabletDimensions[1] || desktopDimensions[1] || defaultDimensions[3]"
    />
    <source
      :srcset="getSources[2]"
      media="(min-width: 768px)"
      :width="tabletDimensions[0] || desktopDimensions[0] || defaultDimensions[2]"
      :height="tabletDimensions[1] || desktopDimensions[1] || defaultDimensions[2]"
    />
    <source
      :srcset="getSources[1]"
      media="(min-width: 431px)"
      :width="mobileDimensions[0] || desktopDimensions[0] || defaultDimensions[1]"
      :height="mobileDimensions[1] || desktopDimensions[1] || defaultDimensions[1]"
    />
    <img
      :src="getSources[0]"
      :alt="altText"
      draggable="false"
      :loading="lazyLoading"
      :width="mobileDimensions[0] || desktopDimensions[0] || defaultDimensions[0]"
      :height="mobileDimensions[1] || desktopDimensions[1] || defaultDimensions[0]"
      :fetchpriority="fetchpriority ?? null"
      @error="onError"
      @load="onLoad"
    />
  </picture>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import useMedia from '../../../utils/useMedia';
import { useSources, type BreakpointCustomParams } from '../../../composables/useSources';

const { isMedia: isTablet } = useMedia('(min-width: 768px)');
const { isMedia: isDesktop } = useMedia('(min-width: 1024px)');

interface BaseImageProps {
  mobileUrl?: string;
  mobileDimensions?: number[];
  tabletUrl?: string;
  tabletDimensions?: number[];
  desktopUrl: string;
  desktopDimensions?: number[];
  mobileAlt?: string;
  tabletAlt?: string;
  desktopAlt: string;
  fullWidth?: boolean;
  lazyLoading?: 'eager' | 'lazy';
  fallbackParam?: string | Record<string, string>;
  customParams?: BreakpointCustomParams;
  fetchpriority?: string;
  preloadImageName?: string;
}

const props = withDefaults(defineProps<BaseImageProps>(), {
  mobileDimensions: () => [],
  tabletDimensions: () => [],
  desktopDimensions: () => [],
  fullWidth: false,
  fallbackParam: 'w',
  customParams: () => ({
    mobileS: null,
    mobile: null,
    tablet: null,
    desktop: null,
    fullWidthDesktop: null,
  }),
  preloadImageName: '',
});

const emit = defineEmits<{
  error: [e: Event];
  load: [e: Event];
}>();
const onError = (e: Event): void => emit('error', e);
const onLoad = (e: Event): void => emit('load', e);

const { getSources, defaultDimensions } = useSources({
  componentName: props.preloadImageName,
  desktopUrl: toRef(() => props.desktopUrl),
  tabletUrl: toRef(() => props.tabletUrl),
  mobileUrl: toRef(() => props.mobileUrl),
  customParams: props.customParams,
  fallbackParam: props.fallbackParam,
  skipLargestBreakpoint: !props.fullWidth,
});

const altText = computed(() => {
  if (isDesktop.value) return props.desktopAlt;
  if (isTablet.value) return props.tabletAlt ?? props.desktopAlt;
  return props.mobileAlt ?? props.desktopAlt;
});
</script>
